import { useEffect, useRef } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';

import getFeatureDecision from '../../../../../config';
import { ENABLE_CHEAPEST_DATES } from '../../../../../config/constants';
import {
  logErrorWrapper,
  logWarnWrapper,
} from '../../../../../services/logWrapper';
import * as analyticsConstants from '../../../helpers/constants/analyticsConstants';
import * as constants from '../../../helpers/constants/dataTypeConstants';
import { useDateSelection } from '../../../helpers/hooks/useDateSelection';
import { useDirectnessFilter } from '../../../helpers/hooks/useDirectnessFilter';
import monthViewServiceQueryHelper from '../../../helpers/interface-handling/monthViewServiceQueryBuilder';
import {
  getBaseUrl,
  getJhaToken,
  getSelectedMonth,
  getSelectedOutboundMonth,
  isWholeYearSelected,
} from '../../../selectors';

import {
  monthViewPageLoaded,
  requestDataErrorAnalysis,
  requestInitialDataAnalytics,
  requestMonthDataBlocked,
  requestMonthDataStarted,
  requestMonthDataSucceeded,
  setPriceSourceId,
} from './monthViewFlightDataActions';
import {
  dispatchPriceTraces,
  getMonthDataMetaData,
  getMonthDataPayload,
  parseWholeYearDates,
  processReceivedMonthData,
} from './monthViewFlightDataHelpers';

import type { State } from '../../../selectors/types';

export const useFlightPricing = () => {
  const dispatch = useDispatch();
  const store = useStore<State>();
  const baseUrl = useSelector(getBaseUrl);
  const jhaToken = useSelector(getJhaToken);
  const outboundMonth = useSelector(getSelectedOutboundMonth);
  const inboundMonth = useSelector((state: State) =>
    getSelectedMonth(state, constants.INBOUND_DIRECTION),
  );
  const isWholeYear = useSelector(isWholeYearSelected);
  const { selectMonthsFromDates } = useDateSelection();
  const { setFilterEnabled, toggleFilter } = useDirectnessFilter();
  const isInitialLoadRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!baseUrl) {
      return;
    }

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    if (isInitialLoadRef.current) {
      dispatch(requestInitialDataAnalytics());
      dispatch(monthViewPageLoaded());
      isInitialLoadRef.current = false;
    }

    const state = store.getState();
    const wholeYear = isWholeYearSelected(state);
    const url = wholeYear
      ? monthViewServiceQueryHelper.buildWholeYearQueryString(state)
      : monthViewServiceQueryHelper.buildMonthsQueryString(state);

    dispatch(requestMonthDataStarted());

    const doFetch = async () => {
      try {
        // If present, adding OverrideConfig header in the URL query parameters
        const overrideConfig = new URLSearchParams(window.location.search).get(
          'OverrideConfig',
        );
        const headersMap: Record<string, string> = {};
        if (jhaToken) headersMap['Skyscanner-JHA'] = jhaToken;
        if (overrideConfig)
          headersMap['Skyscanner-Override-Config'] = overrideConfig;

        const response = await fetch(url, { headers: headersMap });

        if (isStale()) return;

        if (!response.ok) {
          if (response.status === 403) {
            logWarnWrapper({
              message: `requestMonths blocked with status 403`,
              component: 'useFlightPricing',
              url,
            });
            dispatch(requestMonthDataBlocked());
            return;
          }

          dispatch(requestDataErrorAnalysis(response));

          throw new Error(
            `requestMonths failed with status ${response.status}`,
            {
              cause: {
                statusText: response.statusText,
                status: response.status,
                url,
              },
            },
          );
        }

        const json = await response.json();

        if (isStale()) return;

        const callMetadata = getMonthDataMetaData(json);
        callMetadata[analyticsConstants.BROWSE_SERVICE_URL] = url;
        const enableCheapestDates = getFeatureDecision(
          ENABLE_CHEAPEST_DATES,
          false,
        );
        const currentOutboundMonth = getSelectedMonth(
          state,
          constants.OUTBOUND_DIRECTION,
        );

        if (wholeYear) {
          const { inboundDate, isReturn, outboundDate } =
            parseWholeYearDates(json);
          selectMonthsFromDates(outboundDate, inboundDate, isReturn);
        }

        dispatchPriceTraces(json, dispatch);

        processReceivedMonthData({
          grid: getMonthDataPayload(json),
          url,
          callMetadata,
          enableCheapestDates,
          currentOutboundMonth,
          dispatch,
          getState: store.getState,
          toggleFilter,
          setFilterEnabled,
        });

        dispatch(setPriceSourceId(callMetadata.PriceSourceId));
        dispatch(requestMonthDataSucceeded());
      } catch (error) {
        if (isStale()) return;

        dispatch(requestMonthDataSucceeded());

        logErrorWrapper(error as Error, {
          message: 'requestMonths error',
          component: 'useFlightPricing',
          url: (error as Error & { cause?: { url?: string } }).cause?.url,
          status: (error as Error & { cause?: { status?: number } }).cause
            ?.status,
          statusText: (error as Error & { cause?: { statusText?: string } })
            .cause?.statusText,
        });
      }
    };

    doFetch();
  }, [
    baseUrl,
    jhaToken,
    outboundMonth,
    inboundMonth,
    isWholeYear,
    selectMonthsFromDates,
    toggleFilter,
    setFilterEnabled,
    dispatch,
    store,
  ]);
};
